import {
  DelegationChain,
  DelegationIdentity,
  Ed25519KeyIdentity,
  JsonnableDelegationChain,
  JsonnableEd25519KeyIdentity,
} from '@icp-sdk/core/identity'
import { JsonableSecp256k1Identity, Secp256k1KeyIdentity } from '@icp-sdk/core/identity/secp256k1'

export type SerializedIdentity =
  | {
      type: 'ed25519'
      data: JsonnableEd25519KeyIdentity
    }
  | {
      type: 'secp256k1'
      data: JsonableSecp256k1Identity
    }
  | {
      type: 'delegation'
      data: {
        sessionKey: JsonnableEd25519KeyIdentity
        delegationChain: JsonnableDelegationChain
      }
    }

export const serializeIdentity = (
  identity: Ed25519KeyIdentity | Secp256k1KeyIdentity | DelegationIdentity
): SerializedIdentity => {
  if ('getDelegation' in identity && typeof identity.getDelegation === 'function') {
    const delegationIdentity = identity as DelegationIdentity & {
      __sessionKey__?: Ed25519KeyIdentity
    }
    const sessionKey = delegationIdentity.__sessionKey__

    if (!sessionKey) {
      throw new Error(
        'DelegationIdentity missing session key context. Ensure identity was created by DelegationKeychain.'
      )
    }

    return {
      type: 'delegation',
      data: {
        sessionKey: sessionKey.toJSON(),
        delegationChain: delegationIdentity.getDelegation().toJSON(),
      },
    }
  } else if (identity.constructor.name === 'Ed25519KeyIdentity') {
    return {
      type: 'ed25519',
      data: (identity as Ed25519KeyIdentity).toJSON(),
    }
  } else {
    return {
      type: 'secp256k1',
      data: (identity as Secp256k1KeyIdentity).toJSON(),
    }
  }
}

export const deserializeIdentity = ({
  type,
  data,
}: SerializedIdentity): Ed25519KeyIdentity | Secp256k1KeyIdentity | DelegationIdentity => {
  switch (type) {
    case 'ed25519':
      return Ed25519KeyIdentity.fromJSON(JSON.stringify(data))

    case 'secp256k1':
      return Secp256k1KeyIdentity.fromParsedJson(data)

    case 'delegation': {
      const sessionKey = Ed25519KeyIdentity.fromJSON(JSON.stringify(data.sessionKey))
      const delegationChain = DelegationChain.fromJSON(data.delegationChain)
      return DelegationIdentity.fromDelegation(sessionKey, delegationChain)
    }

    default:
      throw new Error(`Unknown identity type: ${type}`)
  }
}
